import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import { useT } from "@/i18n/TranslationContext";

const ContactForm = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!values.name.trim()) e.name = t("contact.form.required");
    if (!values.email.trim()) e.email = t("contact.form.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      e.email = t("contact.form.invalid_email");
    }
    if (!values.message.trim()) e.message = t("contact.form.required");
    return e;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    // Frontend-only: redirect to thank_you
    setTimeout(() => {
      navigate("/thank_you");
    }, 250);
  };

  const inputCls = (hasError) =>
    `w-full rounded-xl border ${
      hasError ? "border-[#c46a3d]" : "border-[#0b2545]/20"
    } bg-white px-4 py-3 text-[#0b2545] placeholder-[#0b2545]/40 focus:outline-none focus:ring-2 focus:ring-[#0b2545]/25 focus:border-[#0b2545] transition`;

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="bg-white/95 backdrop-blur rounded-2xl border border-[#0b2545]/12 shadow-xl p-6 sm:p-8"
      data-testid="contact-form"
    >
      <div className="grid gap-4">
        <div>
          <label htmlFor="cf-name" className="block text-xs font-semibold text-[#0b2545]/80 uppercase tracking-widest mb-1.5">
            {t("contact.form.name")}
          </label>
          <input
            id="cf-name"
            type="text"
            value={values.name}
            onChange={(e) => setValues((v) => ({ ...v, name: e.target.value }))}
            placeholder={t("contact.form.name_placeholder")}
            className={inputCls(errors.name)}
            data-testid="contact-name"
          />
          {errors.name && <p className="text-xs text-[#c46a3d] mt-1">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="cf-email" className="block text-xs font-semibold text-[#0b2545]/80 uppercase tracking-widest mb-1.5">
            {t("contact.form.email")}
          </label>
          <input
            id="cf-email"
            type="email"
            value={values.email}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
            placeholder={t("contact.form.email_placeholder")}
            className={inputCls(errors.email)}
            data-testid="contact-email"
          />
          {errors.email && <p className="text-xs text-[#c46a3d] mt-1">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="cf-message" className="block text-xs font-semibold text-[#0b2545]/80 uppercase tracking-widest mb-1.5">
            {t("contact.form.message")}
          </label>
          <textarea
            id="cf-message"
            rows={5}
            value={values.message}
            onChange={(e) => setValues((v) => ({ ...v, message: e.target.value }))}
            placeholder={t("contact.form.message_placeholder")}
            className={inputCls(errors.message) + " resize-y"}
            data-testid="contact-message"
          />
          {errors.message && <p className="text-xs text-[#c46a3d] mt-1">{errors.message}</p>}
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="mt-2 inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 rounded-full bg-[#0b2545] hover:bg-[#13315c] disabled:opacity-70 text-white font-semibold text-sm transition"
          data-testid="contact-submit"
        >
          <Send className="w-4 h-4" /> {t("contact.form.submit")}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
